import type { IElementConfig } from '../../element/types';

import { merge } from '../../../helper';

import addClass from '../../../general/class/addClass';
import isEmptyObject from '../../../general/isEmpty/isEmptyObject';

import exLanguage from '../../../language';
import * as exDropdown from '../../../components/dropdown';
import * as exModal from '../../../components/modal';
import * as exInput from '../../../forms/input';
import * as exElement from '../../element';
import * as exIcon from '../../icon';
import * as exLayout from '../../layout/index';

import * as exUtils from '../utils';
import type { IEventListener, TypeEventListenerReturn } from '../utils/eventListener';

// ----------------------------------------------------------------------------

/*
 * A way into the picker. `html` is the whole of a plain menu entry; `title` and
 * `subtitle` build the two-line one instead, which is what a source needing to say
 * what it accepts is given.
 */
export type TypeDropzoneButton = {
    enabled?: boolean;
    multiple?: boolean;
    html?: string;
    icon?: string;
    title?: string;
    subtitle?: string;
};

export interface ICreateConfig extends IElementConfig {
    merge?: any;
    size?: 'default' | 'lg';
    /*
     * Whether a click anywhere in the zone opens the default picker. Off by default:
     * the zone is as large as its panel, and a target that size opening a file dialog
     * with nothing on screen saying it would is a surprise. Turn it on for a zone
     * with no button of its own, and say so in the title.
     */
    clickable?: boolean;
    title?: string;
    subtitle?:
        | boolean
        | {
              title?: string;
          };
    accept?: string[];
    input?: {
        type?: 'file' | 'folder';
        html?: string;
    };
    icon?: {
        enabled?: boolean;
        type?: string;
    };
    draggable?: {
        enabled?: boolean;
        title?: string;
        subtitle?: string;
    };
    buttons?: {
        [key: string]: TypeDropzoneButton | undefined;
        file?: TypeDropzoneButton;
        folder?: TypeDropzoneButton;
    };

    alertModal?: boolean | Record<string, any>;
    dropdown?: Record<string, any>;
    fnChange?: TypeEventListenerReturn;
    change?: IEventListener;
}

export type TypeCreateReturn = {
    element: HTMLElement;
    elements: {
        element: HTMLElement;
        dropzoneLeft: HTMLElement;
        dropzoneMiddle: HTMLElement;
        dropzoneRight: HTMLElement;
        iconBox?: HTMLElement;
        icon?: HTMLElement;
        title: HTMLElement;
        subtitle: HTMLElement;
        dropdown?: Record<string, any>;
    };
};

// ----------------------------------------------------------------------------

function create(config: ICreateConfig = {}): TypeCreateReturn {
    const defConfig: Record<string, any> = {
        size: 'default',
        clickable: false,
        subtitle: true,
        /*
         * No html here: the line it would carry depends on `clickable`, which is not
         * settled until the caller's config is merged, and a default written in ahead
         * of that cannot be told apart from one the caller asked for.
         */
        input: {
            type: 'file',
        },
        icon: {
            enabled: true,
            type: 'fa-solid fa-cloud-arrow-up fa-2x',
        },
        draggable: {
            title: exLanguage.line('upload.drop_file_upload'),
        },
        buttons: {
            enabled: true,
            file: {
                enabled: true,
                attr: {
                    name: 'import-file-upload',
                },
                html: exLanguage.line('general.file'),
                multiple: true,
            },
            folder: {
                enabled: true,
                attr: {
                    name: 'import-folder-upload',
                },
                html: exLanguage.line('general.folder'),
                directory: true,
                multiple: true,
            },
            url: {
                enabled: false,
                html: exLanguage.line('general.url'),
            },
            server: {
                enabled: false,
                html: exLanguage.line('general.server'),
            },
        },
    };
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    const build = {
        elements: {},
    } as TypeCreateReturn;

    // ------------------------------------------------------------------------

    const layout = exLayout.create({
        merge: defConfig,
        prefix: 'upload-dropzone',
        layoutLeft: defConfig.dropzoneLeft,
        layoutMiddle: defConfig.dropzoneMiddle,
        layoutRight: defConfig.dropzoneRight,
    });
    if (defConfig.size === 'lg') {
        addClass(layout.element, 'upload-dropzone-lg');
    }

    // What carries the pointer cursor, so only a zone that answers a click shows one.
    if (defConfig.clickable === true) {
        addClass(layout.element, 'upload-dropzone-clickable');
    }

    build.element = layout.element;
    build.elements.element = layout.element;
    build.elements.dropzoneLeft = layout.elements.layoutLeft;
    build.elements.dropzoneMiddle = layout.elements.layoutMiddle;
    build.elements.dropzoneRight = layout.elements.layoutRight;

    // ------------------------------------------------------------------------

    // Create element (icon-box + icon) → left slot
    if (defConfig.icon?.enabled !== false) {
        const elIconBox = exElement.create({
            merge: defConfig.dropzoneIconBox,
            class: {
                new: 'upload-dropzone-icon-box',
            },
        });
        layout.elements.layoutLeft.appendChild(elIconBox);
        build.elements.iconBox = elIconBox;

        const elIcon = exIcon.create({
            merge: defConfig.dropzoneIcon,
            type: defConfig.icon?.type,
            class: {
                new: 'upload-dropzone-icon',
            },
        });
        elIconBox.appendChild(elIcon);
        build.elements.icon = elIcon;
    }

    // Create element (title) → middle slot
    const elTitle = exElement.create({
        merge: defConfig.dropzoneTitle,
        class: {
            new: 'upload-dropzone-title',
        },
    });
    layout.elements.layoutMiddle.appendChild(elTitle);
    build.elements.title = elTitle;

    // Create element (subtitle) → middle slot
    const elSubtitle = exElement.create({
        merge: defConfig.dropzoneSubtitle,
        class: {
            new: 'upload-dropzone-info',
        },
    });
    layout.elements.layoutMiddle.appendChild(elSubtitle);
    build.elements.subtitle = elSubtitle;

    // ------------------------------------------------------------------------

    // Settings
    settings(build, defConfig as ICreateConfig);

    // ------------------------------------------------------------------------

    // Return
    return build;
}

function settings(build: TypeCreateReturn, config: ICreateConfig = {}): void {
    const elements = build.elements;

    // If using change with accept
    if (!isEmptyObject(config.change) && config.change?.accept != null) {
        config.accept = config.change.accept;
    }

    // ------------------------------------------------------------------------

    // If using alert modal
    if (config.alertModal === true || typeof config.alertModal === 'object') {
        const configAlertModal = config.alertModal === true ? {} : config.alertModal;

        if (config.change) {
            const originalFnError = config.change.fnError;

            config.change.fnError = async function (rejected, result): Promise<void> {
                if (typeof originalFnError === 'function') {
                    await originalFnError(rejected, result);
                }

                const title = configAlertModal.title ?? exLanguage.line('general.error');
                const message =
                    configAlertModal.message ??
                    exLanguage.line('upload.not_file_extension', {
                        ext: config.accept?.join(', ') ?? '',
                    });

                exModal.validation({
                    merge: configAlertModal,
                    type: 'danger',
                    title: title,
                    message: message,
                });
            };
        }
    }

    // ------------------------------------------------------------------------

    // If not using function change, is default
    if (!(typeof config.fnChange === 'function')) {
        config.fnChange = exUtils.eventListener({
            merge: config.change,
        });
    }

    // ------------------------------------------------------------------------

    // If using subtitle
    if (config.subtitle === true || typeof config.subtitle === 'object') {
        //
        if (typeof config.subtitle === 'object' && typeof config.subtitle.title === 'string') {
            elements.subtitle.textContent = config.subtitle.title;
        }
        //
        else if (config.accept != null) {
            const accept: string[] = Array.isArray(config.accept) ? config.accept.map(String) : [];

            elements.subtitle.innerHTML = exLanguage.line('upload.files_allowed', { ext: accept.join(', ') });
        }
    }

    // ------------------------------------------------------------------------

    /*
     * The line the zone leads with, when the caller has not written one. It follows
     * `clickable`: "or click to upload" over a zone that answers no click is an offer
     * that goes nowhere, and it is the default, so it is the one most likely to be
     * left as it stands.
     */
    if (config.input != null && config.input.html == null) {
        const isFolder = config.input.type === 'folder';

        if (config.clickable === true) {
            config.input.html = exLanguage.line(
                isFolder ? 'upload.drop_or_input_folder_upload' : 'upload.drop_or_input_file_upload',
            );
        } else {
            config.input.html = exLanguage.line(isFolder ? 'upload.drop_folder' : 'upload.drop_file');
        }
    }

    // ------------------------------------------------------------------------

    // If not using draggable
    if (isEmptyObject(config.draggable)) {
        elements.title.innerHTML = config.input?.html ?? '';
    }
    // If using draggable
    else {
        const draggable = config.draggable ?? {};

        // If input type is folder
        if (config.input?.type === 'folder' && config.draggable) {
            config.draggable.title = exLanguage.line('upload.drop_folder_upload');
        }

        const elRoot = elements.element instanceof HTMLElement ? elements.element : null;
        if (elRoot == null) {
            throw new Error('upload-dropzone is not an HTMLElement');
        }

        const normalTitle: string = config.title ?? config.input?.html ?? '';
        const draggableEnterHtml: string = typeof draggable.title === 'string' ? draggable.title : normalTitle;
        const normalSubtitle: string = elements.subtitle.innerHTML;
        const draggableEnterSubtitle: string | null =
            typeof draggable.subtitle === 'string' ? draggable.subtitle : null;

        exUtils.draggableEvent(elRoot, {
            fnDragEnter: function (): void {
                elements.title.innerHTML = draggableEnterHtml;
                if (draggableEnterSubtitle != null) {
                    elements.subtitle.innerHTML = draggableEnterSubtitle;
                }
            },
            fnDragLeave: function (): void {
                elements.title.innerHTML = normalTitle;
                if (draggableEnterSubtitle != null) {
                    elements.subtitle.innerHTML = normalSubtitle;
                }
            },
            fnDrop: function (this: HTMLElement, evt: DragEvent): void {
                elements.title.innerHTML = normalTitle;
                if (draggableEnterSubtitle != null) {
                    elements.subtitle.innerHTML = normalSubtitle;
                }
                if (typeof config.fnChange === 'function') {
                    void config.fnChange(evt);
                }
            },
        });

        elements.title.innerHTML = normalTitle;
    }

    // ------------------------------------------------------------------------

    // Is default (no buttons)
    if (isEmptyObject(config.buttons)) {
        const inputUpload = exInput.create({
            merge: config.input,
            accept: config.accept,
            attr: {
                hidden: '',
            },
            event: {
                change: config.fnChange,
            },
            container: {
                parent: elements.dropzoneMiddle,
            },
        });

        if (config.clickable === true && elements.element instanceof HTMLElement) {
            const elInput = inputUpload.element instanceof HTMLElement ? inputUpload.element : null;
            elements.element.addEventListener('click', function (): void {
                elInput?.click();
            });
        }
    }
    // If using buttons
    else {
        const dropdown = exDropdown.create({
            merge: config.dropdown,
            link: {
                class: {
                    new: 'btn btn-outline-secondary',
                },
                // The ellipsis is this button's, not the phrase's: here it opens a menu
                html: `${exLanguage.line('general.open_as')}...`,
            },
            menu: {
                alignment: 'end',
            },
        });
        elements.dropzoneRight.appendChild(dropdown.element);
        elements.dropdown = dropdown;

        // --------------------------------------------------------------------

        for (const key of Object.keys(config.buttons ?? {})) {
            if (key !== 'file' && key !== 'folder') {
                continue;
            }

            const value = config.buttons?.[key];

            if (value?.enabled !== true) {
                continue;
            }

            const inputUpload = exInput.create({
                merge: value,
                type: 'file',
                accept: config.accept,
                attr: {
                    hidden: '',
                },
                event: {
                    change: config.fnChange,
                },
            });

            if (key === config.input?.type && elements.element instanceof HTMLElement) {
                const elInput = inputUpload.element instanceof HTMLElement ? inputUpload.element : null;
                const elDropdown = dropdown.element instanceof HTMLElement ? dropdown.element : null;

                if (config.clickable === true) {
                    elements.element.addEventListener('click', function (evt: MouseEvent): void {
                        if (elDropdown?.contains(evt.target instanceof Node ? evt.target : null) === true) {
                            return;
                        }
                        elInput?.click();
                    });
                }

                /*
                 * A split dropdown has a half that is not the toggle. It stands for the
                 * default source, so it opens that picker rather than the menu — and the
                 * guard above, which leaves everything inside the dropdown to the
                 * dropdown, is what would otherwise leave it doing nothing at all.
                 */
                const elSplitAction =
                    dropdown.elements.split != null && dropdown.elements.link?.element instanceof HTMLElement
                        ? dropdown.elements.link.element
                        : null;

                elSplitAction?.addEventListener('click', function (evt: MouseEvent): void {
                    evt.preventDefault();
                    elInput?.click();
                });
            }

            const dropdownItem = exDropdown.add({
                link: {
                    html: value.html,
                    icon: value.icon,
                    title: value.title,
                    subtitle: value.subtitle,
                    event: {
                        click: function (): any {
                            inputUpload.element.click();
                        },
                    },
                },
                container: {
                    parent: dropdown.elements.menu?.element,
                },
            });
            dropdownItem.element.appendChild(inputUpload.element);
        }
    }
}

// ----------------------------------------------------------------------------

export default create;
